import { useState } from 'react';
import { t } from '@lingui/macro';
import Button from '@material-ui/core/Button';
import {
    PLM_NAME_FIELD,
    PLM_HOST_FIELD,
    PLM_DESCRIPTION_FIELD,
    PLM_CREATED_AT_FIELD,
    PLM_UPDATED_AT_FIELD,
    PLM_ID_FIELD,
    BASE_URL,
    PLM_PAGE_TITLE,
    PLM_AUTO_ACCESS,
} from './PlmConstants';
import { formatDateTime } from './PlmAdminHelper';
import { buildPageTarget } from './V3ApiHelper';
import { SELECTIONMODE_NONE } from '../../data-table/SelectionContext';
import DataTable from '../../data-table/DataTable';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import Actions from '../../page-framework/actions/Actions';
import { SORT_ASC } from '../../../utilities/FormUtilities';

import AdminPage from '../AdminPage';
import { AdminPages, buildPageId, buildCreatePageTarget } from '../AdminLinkUtils';
import Link from '../../links/Link';
import { useIsSysAdminUser } from '../../../stores/SessionStore';
import AuthRoleGuard from '../../auth/AuthRoleGuard';

const functionName = AdminPages.Plms;

function getColumns() {
    return [
        {
            type: COLUMN_TYPES.LINK,
            mapping: PLM_NAME_FIELD,
            title: t`Name`,
            sortable: true,
            width: 280,
            link: {
                target: buildPageTarget(functionName),
            },
        },
        {
            type: COLUMN_TYPES.LINK,
            mapping: PLM_HOST_FIELD,
            title: t`Host`,
            sortable: true,
            width: 280,
            link: {
                target: buildPageTarget(functionName),
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: PLM_DESCRIPTION_FIELD,
            title: t`Description`,
            sortable: true,
            width: 200,
            flex: 1,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: PLM_AUTO_ACCESS,
            title: t`Auto-Access`,
            sortable: true,
            sortProperty: PLM_AUTO_ACCESS,
            mapper: (row) => t`${row?.[PLM_AUTO_ACCESS] ? 'Enabled' : 'Disabled'}`,
            width: 130,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`First Seen`,
            sortable: true,
            sortProperty: PLM_CREATED_AT_FIELD,
            mapper: (row) => formatDateTime(row, PLM_CREATED_AT_FIELD),
            width: 220,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Updated`,
            sortable: true,
            sortProperty: PLM_UPDATED_AT_FIELD,
            mapper: (row) => formatDateTime(row, PLM_UPDATED_AT_FIELD),
            width: 220,
        },
    ];
}

const initialSort = [{ property: PLM_NAME_FIELD, direction: SORT_ASC }];

const dataSource = {
    url: `${BASE_URL}list`,
    totalProperty: 'total',
    dataProperty: 'data',
    params: {
        properties: [
            PLM_NAME_FIELD,
            PLM_HOST_FIELD,
            PLM_DESCRIPTION_FIELD,
            PLM_CREATED_AT_FIELD,
            PLM_UPDATED_AT_FIELD,
            PLM_AUTO_ACCESS,
        ],
    },
};

export default function PlmAdminList() {
    const [, setTableState] = useState(undefined);
    const [isSysAdminUser] = useIsSysAdminUser();

    // Used by the data table to load data.
    const loader = useQueryLoader(dataSource, { disableFilterState: true });

    return (
        <AdminPage
            title={PLM_PAGE_TITLE}
            pageId={buildPageId(functionName)}
            actions={
                isSysAdminUser && (
                    <Actions>
                        <Button
                            component={Link}
                            to={buildCreatePageTarget(functionName)}
                            variant="contained"
                            color="primary"
                        >
                            {t`Create`}
                        </Button>
                    </Actions>
                )
            }
        >
            <AuthRoleGuard allowedRoles={['super']}>
                {isSysAdminUser && (
                    <DataTable
                        loader={loader}
                        columns={getColumns()}
                        tableId={functionName}
                        sorters={initialSort}
                        onStateChanged={setTableState}
                        visibleRows={0}
                        selectionOptions={{
                            mode: SELECTIONMODE_NONE,
                            allowSelectAll: true,
                            idMapping: PLM_ID_FIELD,
                        }}
                    />
                )}
            </AuthRoleGuard>
        </AdminPage>
    );
}
